import axios from 'axios';
import { buildForbiddenFeedback, isFeatureUnavailableError } from '../utils/apiErrorUtils';
import { isSignInRequest } from '../utils/authErrorUtils';
import { markTenantFeatureAsLocked } from '../utils/featureAccessUtils';
import logger from '../utils/logger';

/**
 * Get authentication token from session storage.
 * Prefers application-issued JWT (appToken) when available, falls back to msalToken.
 * @returns {string|null} The authentication token or null if not found
 */
function getToken() {
  const appToken = sessionStorage?.getItem('appToken');
  const msalToken = sessionStorage?.getItem('msalToken');
  
  // Return first available token, validate it's not empty
  const token = appToken || msalToken;
  return token && typeof token === 'string' ? token : null;
}

/**
 * Axios instance configured with base URL and authentication interceptors.
 * Automatically adds Authorization header and handles authentication errors.
 */
const api = axios.create({ baseURL: '/api' });
let lastForbiddenNotice = { key: '', timestamp: 0 };

api.interceptors.request.use(config => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Add tenant context header if available
  const activeTenantId = sessionStorage.getItem('activeTenantId');
  const isSuperAdminEndpoint =
    typeof config.url === 'string' && config.url.includes('/api/superadmin/');
  if (activeTenantId && !isSuperAdminEndpoint) {
    config.headers['X-Tenant-Id'] = activeTenantId;
  }
  return config;
});


api.interceptors.response.use(
  response => response,
  async error => {
    // A canceled access check may finish after switching tenant or signing out.
    if (error.config?.signal?.aborted) return Promise.reject(error);
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const url = error.config?.url || 'unknown';
      const method = error.config?.method?.toUpperCase() || 'unknown';

      if ((status === 401 || status === 403) && isSignInRequest(error)) {
        logger.warn(`[${method} ${url}] Sign-in rejected (${status}).`);
        return Promise.reject(error);
      }

      if (status === 401) {
        if (error.config?.preserveAuthSessionOn401) {
          // Some function routes can reject their own key or route authorization
          // while the user's app session is still valid.
          logger.warn(`[${method} ${url}] Endpoint authorization failed (401); preserving the app session.`);
        } else {
          // Handle a session-level 401 by clearing tokens and returning to login.
          sessionStorage.removeItem('msalToken');
          sessionStorage.removeItem('appToken');
          const isLoginPage = window.location.pathname === '/' || window.location.pathname === '/login';
          if (!isLoginPage) {
            window.location.href = '/';
          }
          logger.warn(`[${method} ${url}] Session expired (401). Redirecting to login.`);
        }
      } else if (status === 403) {
        const feedback = buildForbiddenFeedback(error);
        const noticeKey = `${feedback.endpoint}|${status}`;
        const now = Date.now();
        const lockedFeatures = markTenantFeatureAsLocked({
          featureName: feedback.featureName,
          endpoint: feedback.endpoint,
        });

        // Background entitlement checks must not interrupt the user with an upgrade modal.
        // They still update the tenant-scoped access cache and reject normally to the caller.
        const suppressForbiddenNotice = error.config?.suppressForbiddenNotice === true;
        if (!suppressForbiddenNotice && (noticeKey !== lastForbiddenNotice.key || now - lastForbiddenNotice.timestamp > 2000)) {
          window.dispatchEvent(
            new CustomEvent('tenant-feature-forbidden', {
              detail: {
                status: 403,
                endpoint: feedback.endpoint,
                featureName: feedback.featureName,
                title: feedback.title,
                message: feedback.message,
                isFeatureUnavailable: isFeatureUnavailableError(error),
                lockedFeatures,
              },
            })
          );
          lastForbiddenNotice = { key: noticeKey, timestamp: now };
        }

        logger.warn(`[${method} ${url}] Access forbidden (403).`, feedback);
      } else if (status === 404) {
        logger.warn(`[${method} ${url}] Resource not found (404).`);
      } else {
        logger.error(`[${method} ${url}] Server error (${status}):`, error.response.data);
      }
    } else if (error.request) {
      // Request made but no response received
      logger.error('Network error: No response from server. Request details:', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        message: error.message,
      });
    } else {
      // Error during request setup
      logger.error('Request setup error:', {
        message: error.message,
        stack: error.stack,
      });
    }
    return Promise.reject(error);
  }
);

export default api;
